import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Anchor, ChevronLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="container-app">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">Y GO</span>
            </Link>
            <Link to="/" className="text-slate-300 hover:text-white text-sm font-medium flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container-app py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-black text-slate-900 mb-8">Terms & Conditions</h1>
          
          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Service Description</h2>
              <p className="text-slate-600 mb-4">
                Y GO - Seamen Shore Services provides professional shuttle transport services 
                between port terminals and designated city center locations. Our service is 
                specifically designed for seafarers and maritime personnel during their shore leave.
              </p>
              <p className="text-slate-600">
                We operate 24 hours a day, 7 days a week, serving all major port terminals 
                in the area with professional, port-accredited drivers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Pricing Policy</h2>
              <p className="text-slate-600 mb-4">
                All prices are fixed and displayed per vehicle before booking. Our pricing 
                structure is transparent with no hidden fees.
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Prices vary by terminal location (€40 - €65 per vehicle)</li>
                <li>Night service surcharge of +20% applies between 21:00 and 06:00</li>
                <li>Total price is calculated as: Base price + Night surcharge (if applicable)</li>
                <li>Prices are displayed in EUR; USD conversion is indicative only</li>
                <li>Price is per vehicle, not per passenger — share with your crewmates!</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Booking and Payment</h2>
              <h3 className="text-lg font-bold text-slate-800 mb-2">3.1 Online Payment (Pay Now)</h3>
              <p className="text-slate-600 mb-4">
                Online payments are processed securely through Stripe. Your booking is 
                confirmed immediately upon successful payment. We accept all major credit 
                and debit cards.
              </p>
              
              <h3 className="text-lg font-bold text-slate-800 mb-2">3.2 Cash Payment (Pay to Driver)</h3>
              <p className="text-slate-600 mb-4">
                Cash payments are accepted upon service completion. Please have the exact 
                amount ready for the driver. Only EUR currency is accepted for cash payments.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Cancellation Policy</h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Cancellations made more than 24 hours before pickup: Full refund</li>
                <li>Cancellations made 12-24 hours before pickup: 50% refund</li>
                <li>Cancellations made less than 12 hours before pickup: No refund available</li>
                <li>No-shows will be charged the full amount</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Passenger Conduct</h2>
              <p className="text-slate-600 mb-4">
                To ensure a safe and comfortable journey for all, passengers are expected to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Behave respectfully towards the driver and other passengers</li>
                <li>Refrain from aggressive, abusive, or inappropriate behavior</li>
                <li>Not be under the influence of substances to the point of incapacity</li>
                <li>Follow the driver's instructions regarding safety</li>
                <li>Keep the vehicle clean and not cause damage</li>
              </ul>
              <p className="text-slate-600 mt-4">
                <strong>The driver reserves the right to refuse service</strong> if a passenger's 
                behavior poses a safety risk or violates these conduct guidelines. In such cases, 
                no refund will be provided.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Damage and Cleaning Fees</h2>
              <p className="text-slate-600 mb-4">
                Passengers are responsible for any damage caused to the vehicle during their journey. 
                Additional fees may be charged for:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Excessive cleaning required due to spills, stains, or mess</li>
                <li>Damage to vehicle interior or equipment</li>
                <li>Lost or damaged items belonging to Y GO</li>
              </ul>
              <p className="text-slate-600 mt-4">
                Cleaning fees start at €50, and damage fees will be assessed based on repair costs.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Luggage</h2>
              <p className="text-slate-600 mb-4">
                Standard luggage is included in the service price. Please inform us in advance 
                if you have:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>More than 2 large suitcases per passenger</li>
                <li>Oversized or heavy items</li>
                <li>Special equipment requiring additional space</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Waiting Time</h2>
              <p className="text-slate-600 mb-4">
                Drivers will wait up to 15 minutes past the scheduled pickup time. After this period:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Additional waiting time may be charged at €1 per minute</li>
                <li>The driver may leave and the booking will be considered a no-show</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Liability</h2>
              <p className="text-slate-600 mb-4">
                Y GO - Seamen Shore Services maintains comprehensive insurance coverage for all 
                passengers during their journey. However, we are not liable for:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Delays caused by traffic, weather, or circumstances beyond our control</li>
                <li>Personal items left in the vehicle</li>
                <li>Missed connections or appointments due to any delays</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Data Protection</h2>
              <p className="text-slate-600">
                We collect and process personal data in accordance with applicable data protection 
                laws. Your information is used solely for booking management, service delivery, 
                and communication purposes. We do not share your personal information with third 
                parties except as required to provide our services (e.g., payment processing).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Contact Information</h2>
              <p className="text-slate-600">
                For questions, complaints, or assistance, please contact us at:
              </p>
              <p className="text-slate-600 mt-2">
                <strong>Email:</strong> booking@y-go.fr
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Changes to Terms</h2>
              <p className="text-slate-600">
                We reserve the right to modify these terms and conditions at any time. Changes 
                will be effective immediately upon posting on our website. Continued use of our 
                services constitutes acceptance of any modified terms.
              </p>
            </section>

            <div className="border-t border-slate-200 pt-6 mt-8">
              <p className="text-sm text-slate-500">
                Last updated: {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/booking">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold">
                Book a Shuttle
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 mt-12">
        <div className="container-app text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Y GO - Seamen Shore Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
